import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { getGame, updateGame, getTeam, addPlayer, updateTeam, fetchGames } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Check, Play, Users, MapPin,
  ArrowUp, ArrowDown, X, ScanLine, Plus, Trash2, GripVertical, ClipboardPaste, Home, Shield
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ScanLineupDialog } from '../components/shared/ScanLineupDialog';
import { isRestEnforced, buildRestMap, getPitcherStatus } from '../lib/pitcherRest';

const FIELD_POSITIONS = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
const POSITION_LABELS = {
  P: 'Pitcher', C: 'Catcher', '1B': '1st Base', '2B': '2nd Base',
  '3B': '3rd Base', SS: 'Shortstop', LF: 'Left Field', CF: 'Center Field', RF: 'Right Field'
};
const POS_COORDS = {
  P: { x: 50, y: 58 }, C: { x: 50, y: 82 }, '1B': { x: 72, y: 52 },
  '2B': { x: 58, y: 38 }, '3B': { x: 28, y: 52 }, SS: { x: 42, y: 38 },
  LF: { x: 22, y: 22 }, CF: { x: 50, y: 12 }, RF: { x: 78, y: 22 },
};

// Mini field diagram showing position assignments
const MiniFieldDiagram = ({ positions, players, oppPositions, isOpponent }) => {
  const posMap = isOpponent ? oppPositions : positions;
  const filledCount = isOpponent
    ? Object.values(oppPositions || {}).filter(Boolean).length
    : Object.values(positions || {}).filter(Boolean).length;

  return (
    <div className="relative w-full max-w-[220px] mx-auto" data-testid={isOpponent ? 'opp-field-diagram' : 'field-diagram'}>
      <svg viewBox="0 0 100 100" className="w-full aspect-square">
        <rect width="100" height="100" fill="#166534" rx="4" />
        <path d="M 5 85 Q 50 -5 95 85" fill="#14532d" />
        <path d="M 50 85 L 75 52 L 50 32 L 25 52 Z" fill="#c2885c" opacity="0.6" />
        <line x1="50" y1="85" x2="75" y2="52" stroke="white" strokeWidth="0.5" opacity="0.4" />
        <line x1="75" y1="52" x2="50" y2="32" stroke="white" strokeWidth="0.5" opacity="0.4" />
        <line x1="50" y1="32" x2="25" y2="52" stroke="white" strokeWidth="0.5" opacity="0.4" />
        <line x1="25" y1="52" x2="50" y2="85" stroke="white" strokeWidth="0.5" opacity="0.4" />
        <rect x="48" y="83" width="4" height="4" fill="white" transform="rotate(45 50 85)" />

        {FIELD_POSITIONS.map(pos => {
          const c = POS_COORDS[pos];
          const filled = isOpponent ? !!posMap?.[pos] : !!posMap?.[pos];
          const playerName = isOpponent
            ? posMap?.[pos]
            : (posMap?.[pos] ? players?.find(p => p.id === posMap[pos])?.name?.split(' ')[0] : null);
          const isPitcher = pos === 'P';

          return (
            <g key={pos}>
              <circle cx={c.x} cy={c.y}
                r={filled ? 5 : 4}
                fill={filled ? (isPitcher ? '#d97706' : (isOpponent ? '#dc2626' : '#2563eb')) : '#374151'}
                stroke={filled ? 'white' : '#6b7280'}
                strokeWidth={filled ? '0.6' : '0.3'}
              />
              <text x={c.x} y={c.y + 1.5} textAnchor="middle" fill="white"
                fontSize={filled ? '3.5' : '3'} fontWeight={filled ? 'bold' : 'normal'}>
                {pos}
              </text>
              {playerName && (
                <text x={c.x} y={c.y + 7} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="2.5">
                  {playerName}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <p className="text-center text-[10px] mt-1" style={{
        color: filledCount === 9 ? '#4ade80' : filledCount > 0 ? '#fbbf24' : '#6b7280'
      }}>
        {filledCount}/9 positions filled
      </p>
    </div>
  );
};

export default function GameSetupPage() {
  const navigate = useNavigate();
  const { currentGameId, currentTeamId } = useGameStore();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Sections collapse state
  const [openSections, setOpenSections] = useState({
    lineup: true, opponentLineup: false, fieldPositions: false, opponentFieldPositions: false
  });

  // Your team lineup (ordered array of player objects)
  const [battingOrder, setBattingOrder] = useState([]);
  const [benchPlayers, setBenchPlayers] = useState([]);

  // Opponent lineup
  const [opponentLineup, setOpponentLineup] = useState([]);
  const [newOppName, setNewOppName] = useState('');
  const [newOppNumber, setNewOppNumber] = useState('');
  const [newMyPlayerName, setNewMyPlayerName] = useState('');
  const [newMyPlayerNum, setNewMyPlayerNum] = useState('');

  // Field positions (your team)
  const [positions, setPositions] = useState({});

  // Opponent field positions
  const [oppPositions, setOppPositions] = useState({});

  // Scan dialogs
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState('team'); // 'team' | 'opponent'

  // Paste opponent dialog
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Home team
  const [isHome, setIsHome] = useState(true);

  // Opponent -> fielding assignment dialog
  const [assignOppFieldDialog, setAssignOppFieldDialog] = useState({ open: false, oppName: '', oppNumber: '' });
  const [assigningOppPos, setAssigningOppPos] = useState(null);
  const [assigningMyPos, setAssigningMyPos] = useState(null);
  const [newPitcherInput, setNewPitcherInput] = useState({ show: false, pos: null, name: '', isOpp: false });
  const [restMap, setRestMap] = useState({});
  const [pitcherRestWarn, setPitcherRestWarn] = useState({ open: false, pitcher: null, status: null, pos: null });

  // Completion tracking
  const lineupComplete = battingOrder.length >= 1;
  const oppLineupComplete = opponentLineup.length >= 1;
  const fieldComplete = Object.keys(positions).length >= 1;
  const oppFieldComplete = Object.keys(oppPositions).length >= 1;

  const loadData = useCallback(async () => {
    if (!currentGameId || !currentTeamId) return;
    try {
      setLoading(true);
      const [gameData, teamData] = await Promise.all([
        getGame(currentGameId),
        getTeam(currentTeamId)
      ]);
      setGame(gameData);
      setTeam(teamData);

      // Pre-game pitcher availability (skips when rest rules are off)
      if (isRestEnforced(teamData)) {
        fetchGames(currentTeamId, gameData.season).then(games => {
          setRestMap(buildRestMap(games || [], currentGameId));
        }).catch(() => setRestMap({}));
      } else {
        setRestMap({});
      }

      // Load batting order
      if (gameData.batting_order?.length) {
        const ordered = gameData.batting_order
          .map(pid => teamData.players?.find(p => p.id === pid))
          .filter(Boolean);
        setBattingOrder(ordered);
        const orderSet = new Set(gameData.batting_order);
        setBenchPlayers(teamData.players?.filter(p => !orderSet.has(p.id)) || []);
      } else {
        setBattingOrder([]);
        setBenchPlayers(teamData.players || []);
      }

      // Load opponent lineup
      if (gameData.opponent_lineup?.length) {
        setOpponentLineup(gameData.opponent_lineup);
      }

      // Load field positions
      if (gameData.field_positions?.length) {
        const latest = gameData.field_positions[gameData.field_positions.length - 1];
        if (latest?.positions) {
          const posMap = {};
          latest.positions.forEach(p => { if (p.player_id) posMap[p.position] = p.player_id; });
          setPositions(posMap);
        }
      }

      // Load opponent field positions
      if (gameData.opponent_field_positions) {
        setOppPositions(gameData.opponent_field_positions);
      }

      // Load home team status
      setIsHome(gameData.is_home !== false);
    } catch (err) {
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  }, [currentGameId, currentTeamId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Auto-save helper
  const saveToBackend = async (updates) => {
    if (!currentGameId) return;
    try {
      setSaving(true);
      await updateGame(currentGameId, updates);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // === BATTING ORDER ===
  const movePlayer = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= battingOrder.length) return;
    const newOrder = [...battingOrder];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    setBattingOrder(newOrder);
    saveToBackend({ batting_order: newOrder.map(p => p.id) });
  };

  const addToLineup = (player) => {
    const newOrder = [...battingOrder, player];
    setBattingOrder(newOrder);
    setBenchPlayers(benchPlayers.filter(p => p.id !== player.id));
    saveToBackend({ batting_order: newOrder.map(p => p.id) });
  };

  const removeFromLineup = (player) => {
    const newOrder = battingOrder.filter(p => p.id !== player.id);
    setBattingOrder(newOrder);
    setBenchPlayers([...benchPlayers, player]);
    saveToBackend({ batting_order: newOrder.map(p => p.id) });
  };

  // === OPPONENT LINEUP ===
  const addOpponent = () => {
    if (!newOppName.trim()) return;
    const newLineup = [...opponentLineup, { name: newOppName.trim(), number: newOppNumber.trim() || '' }];
    setOpponentLineup(newLineup);
    setNewOppName('');
    setNewOppNumber('');
    saveToBackend({ opponent_lineup: newLineup });
  };

  // Add new player to your team roster + batting order
  const handleAddMyPlayer = async () => {
    if (!newMyPlayerName.trim()) return;
    try {
      const created = await addPlayer(currentTeamId, { name: newMyPlayerName.trim(), jersey_number: newMyPlayerNum.trim() || '' });
      const newOrder = [...battingOrder, created];
      setBattingOrder(newOrder);
      setNewMyPlayerName('');
      setNewMyPlayerNum('');
      saveToBackend({ batting_order: newOrder.map(p => p.id) });
      const freshTeam = await getTeam(currentTeamId);
      setTeam(freshTeam);
      const orderSet = new Set(newOrder.map(p => p.id));
      setBenchPlayers(freshTeam.players?.filter(p => !orderSet.has(p.id)) || []);
      toast.success(`${created.name} added`);
    } catch { toast.error('Failed to add player'); }
  };

  const removeOpponent = (idx) => {
    const newLineup = opponentLineup.filter((_, i) => i !== idx);
    setOpponentLineup(newLineup);
    saveToBackend({ opponent_lineup: newLineup });
  };

  const moveOpponent = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= opponentLineup.length) return;
    const newLineup = [...opponentLineup];
    [newLineup[fromIndex], newLineup[toIndex]] = [newLineup[toIndex], newLineup[fromIndex]];
    setOpponentLineup(newLineup);
    saveToBackend({ opponent_lineup: newLineup });
  };

  // === FIELD POSITIONS ===
  const doAssignPosition = (pos, playerId) => {
    const newPos = { ...positions };
    // Remove player from old position if they had one
    if (playerId) {
      Object.keys(newPos).forEach(k => { if (newPos[k] === playerId && k !== pos) delete newPos[k]; });
    }
    if (playerId) newPos[pos] = playerId;
    else delete newPos[pos];
    setPositions(newPos);

    // Build field_positions array
    const posList = FIELD_POSITIONS.map(p => ({
      position: p,
      player_id: newPos[p] || null,
      player_name: newPos[p] ? team?.players?.find(pl => pl.id === newPos[p])?.name : null
    }));
    saveToBackend({ field_positions: [{ inning: 1, positions: posList }] });
    // If pitcher was assigned, also set current_pitcher_id
    if (pos === 'P' && playerId) {
      saveToBackend({ current_pitcher_id: playerId });
    } else if (pos === 'P' && !playerId) {
      saveToBackend({ current_pitcher_id: null });
    }
  };

  const assignPosition = (pos, playerId) => {
    if (playerId === '__new__') {
      setNewPitcherInput({ show: true, pos, name: '', isOpp: false });
      return;
    }
    // Intercept only when assigning an existing roster player to Pitcher (P) and rest rules are on
    if (pos === 'P' && playerId && isRestEnforced(team)) {
      const status = getPitcherStatus(restMap, playerId);
      if (status.status === 'locked' || status.status === 'caution') {
        const pitcher = team?.players?.find(pl => pl.id === playerId);
        setPitcherRestWarn({ open: true, pitcher, status, pos });
        return;
      }
    }
    doAssignPosition(pos, playerId);
  };

  // === OPPONENT FIELD POSITIONS ===
  const assignOppPosition = (pos, value) => {
    const newPos = { ...oppPositions };
    if (value) newPos[pos] = value;
    else delete newPos[pos];
    setOppPositions(newPos);
    saveToBackend({ opponent_field_positions: newPos });
  };

  // Add new pitcher/player to roster from typed name
  const handleNewPitcherConfirm = async () => {
    const name = newPitcherInput.name.trim();
    if (!name) return;
    if (newPitcherInput.isOpp) {
      // Add to opponent lineup + assign position
      const newLineup = [...opponentLineup, { name, number: '' }];
      setOpponentLineup(newLineup);
      assignOppPosition(newPitcherInput.pos, name);
      saveToBackend({ opponent_lineup: newLineup });
      toast.success(`${name} added as opponent ${newPitcherInput.pos}`);
    } else {
      // Add to our team roster + assign position
      try {
        const created = await addPlayer(currentTeamId, { name, position: newPitcherInput.pos });
        assignPosition(newPitcherInput.pos, created.id);
        const freshTeam = await getTeam(currentTeamId);
        setTeam(freshTeam);
        toast.success(`${name} added to roster as ${newPitcherInput.pos}`);
      } catch { toast.error('Failed to add player'); }
    }
    setNewPitcherInput({ show: false, pos: null, name: '', isOpp: false });
  };

  // === PASTE OPPONENT LINEUP ===
  const handlePasteOpponent = () => {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    const newLineup = [...opponentLineup];
    for (const line of lines) {
      // Parse formats: "Name #Number", "#Number Name", "Name", "1. Name", "Name - #5"
      const cleaned = line.replace(/^\d+[\.\)\-\s]+/, '').trim();
      const numMatch = cleaned.match(/#?(\d{1,3})\s*[-–]?\s*(.+)|(.+?)\s*[-–]?\s*#?(\d{1,3})$/);
      let name, number = '';
      if (numMatch) {
        if (numMatch[2]) { number = numMatch[1]; name = numMatch[2].trim(); }
        else { name = numMatch[3].trim(); number = numMatch[4]; }
      } else {
        name = cleaned;
      }
      if (name && !newLineup.find(o => o.name.toLowerCase() === name.toLowerCase())) {
        newLineup.push({ name, number });
      }
    }
    setOpponentLineup(newLineup);
    saveToBackend({ opponent_lineup: newLineup });
    setPasteOpen(false);
    setPasteText('');
    toast.success(`${newLineup.length - opponentLineup.length} opponent(s) added`);
  };

  // === HOME TEAM TOGGLE ===
  const handleHomeToggle = (val) => {
    setIsHome(val);
    saveToBackend({ is_home: val });
  };

  // === REST RULES TOGGLE ===
  const handleRestRulesToggle = async () => {
    const next = !isRestEnforced(team);
    try {
      const updated = await updateTeam(currentTeamId, { enforce_pitch_rest_rules: next });
      setTeam(updated);
      toast.success(`Little League Rest Rules ${next ? 'ON' : 'OFF'}`);
      // Refresh rest map for the availability card
      if (next) {
        fetchGames(currentTeamId, game?.season).then(games => {
          setRestMap(buildRestMap(games || [], currentGameId));
        }).catch(() => setRestMap({}));
      } else {
        setRestMap({});
      }
    } catch {
      toast.error('Failed to update rest rules');
    }
  };

  // === OPPONENT NAME → FIELDING ===
  const handleOppNameClick = (opp) => {
    setAssignOppFieldDialog({ open: true, oppName: opp.name, oppNumber: opp.number || '' });
  };

  const assignOppToField = (pos) => {
    const newPos = { ...oppPositions };
    newPos[pos] = assignOppFieldDialog.oppName;
    setOppPositions(newPos);
    saveToBackend({ opponent_field_positions: newPos });
    setAssignOppFieldDialog({ open: false, oppName: '', oppNumber: '' });
    toast.success(`${assignOppFieldDialog.oppName} → ${POSITION_LABELS[pos]}`);
  };

  // === SCAN LINEUP CARD ===
  const handleScannedPlayers = async (players) => {
    if (scanTarget === 'team') {
      // Add scanned players to team roster & batting order + field positions
      const newBatting = [...battingOrder];
      const fieldPositions = [];
      for (const p of players) {
        const existing = team?.players?.find(tp => tp.name.toLowerCase() === p.name.toLowerCase());
        let player;
        if (!existing) {
          try {
            const created = await addPlayer(currentTeamId, { name: p.name, jersey_number: p.jersey_number || undefined, position: p.position || undefined });
            player = created;
            newBatting.push(created);
          } catch (err) { /* skip duplicates */ }
        } else {
          player = existing;
          if (!battingOrder.find(b => b.id === existing.id)) {
            newBatting.push(existing);
          }
        }
        if (p.position && player) {
          const posMap = { 'C': 'C', '1B': '1B', '2B': '2B', '3B': '3B', 'SS': 'SS', 'LF': 'LF', 'CF': 'CF', 'RF': 'RF', 'P': 'P', 'DH': 'DH' };
          const normalized = posMap[p.position.toUpperCase()] || p.position.toUpperCase();
          fieldPositions.push({ position: normalized, player_id: player.id, player_name: player.name || p.name });
        }
      }
      setBattingOrder(newBatting);
      setBenchPlayers([]);
      const updateData = { batting_order: newBatting.map(pl => pl.id) };
      if (fieldPositions.length > 0) updateData.field_positions = [{ inning: 1, positions: fieldPositions }];
      saveToBackend(updateData);
      const freshTeam = await getTeam(currentTeamId);
      setTeam(freshTeam);
      const orderSet = new Set(newBatting.map(p => p.id));
      setBenchPlayers(freshTeam.players?.filter(p => !orderSet.has(p.id)) || []);
      const posMsg = fieldPositions.length > 0 ? ` + ${fieldPositions.length} field positions` : '';
      toast.success(`${players.length} players scanned${posMsg}`);
    } else {
      // Opponent lineup + field positions
      const newLineup = [...opponentLineup];
      const newOppPos = { ...oppPositions };
      for (const p of players) {
        if (!newLineup.find(o => o.name.toLowerCase() === p.name.toLowerCase())) {
          newLineup.push({ name: p.name, number: p.jersey_number || '' });
        }
        if (p.position) {
          const posMap = { 'C': 'C', '1B': '1B', '2B': '2B', '3B': '3B', 'SS': 'SS', 'LF': 'LF', 'CF': 'CF', 'RF': 'RF', 'P': 'P' };
          const normalized = posMap[p.position.toUpperCase()] || null;
          if (normalized) newOppPos[normalized] = p.name;
        }
      }
      setOpponentLineup(newLineup);
      setOppPositions(newOppPos);
      saveToBackend({ opponent_lineup: newLineup, opponent_field_positions: newOppPos });
      const posCount = Object.values(newOppPos).filter(Boolean).length;
      const posMsg = posCount > 0 ? ` + ${posCount} field positions` : '';
      toast.success(`${players.length} opponents scanned${posMsg}`);
    }
  };

  const handleStartGame = async () => {
    try {
      const startData = { is_top_inning: true, current_batter_index: 0, opponent_current_batter_index: 0 };
      // Carry pitcher from field positions into current_pitcher_id
      if (positions['P']) startData.current_pitcher_id = positions['P'];
      // Also set opponent pitcher name if assigned
      if (oppPositions['P']) startData.opponent_pitcher_name = oppPositions['P'];
      await updateGame(currentGameId, startData);
    } catch {}
    navigate('/game-mode');
  };

  if (!currentGameId || !currentTeamId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900 text-stone-400">
        <div className="text-center space-y-4">
          <p className="text-lg">No game selected</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-green-700 hover:bg-green-600">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const SectionHeader = ({ title, icon: Icon, isOpen, onToggle, complete, count }) => (
    <button
      onClick={onToggle}
      className={cn(
        "w-full flex items-center justify-between p-4 rounded-xl transition-all",
        isOpen ? "bg-stone-700/80" : "bg-stone-800/60 hover:bg-stone-700/50"
      )}
      data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className={complete ? "text-green-400" : "text-stone-500"} />
        <span className="font-bold text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {title}
        </span>
        {count > 0 && (
          <span className="text-xs bg-stone-600 text-stone-300 px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {complete && <Check size={16} className="text-green-400" />}
        {isOpen ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-stone-900 p-4 md:p-6" data-testid="game-setup-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            GAME SETUP
          </h1>
          <p className="text-stone-400 text-sm">
            {game?.is_home ? 'vs' : '@'} <span className="text-amber-400 font-bold">{game?.opponent_name}</span>
            {' '}&middot; {game?.date} &middot; {game?.season}
          </p>
          {saving && <p className="text-green-400 text-xs mt-1 animate-pulse">Saving...</p>}
        </div>

        {/* Home Team Selector */}
        <Card className="border-0 bg-stone-800/50" data-testid="home-team-selector">
          <CardContent className="p-3">
            <p className="text-stone-400 text-[10px] uppercase tracking-wider text-center mb-2 font-bold">Who is the Home Team?</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                className={cn("p-3 rounded-lg border-2 text-center transition-all",
                  isHome ? "border-green-500 bg-green-900/30" : "border-stone-700 bg-stone-800/50 hover:border-stone-600"
                )}
                onClick={() => handleHomeToggle(true)}
                data-testid="home-select-our"
              >
                <p className={cn("text-sm font-bold", isHome ? "text-green-400" : "text-stone-500")}>{team?.name || 'Your Team'}</p>
                {isHome && <p className="text-green-600 text-[9px] mt-0.5">HOME (bats 2nd)</p>}
              </button>
              <button
                className={cn("p-3 rounded-lg border-2 text-center transition-all",
                  !isHome ? "border-red-500 bg-red-900/30" : "border-stone-700 bg-stone-800/50 hover:border-stone-600"
                )}
                onClick={() => handleHomeToggle(false)}
                data-testid="home-select-opp"
              >
                <p className={cn("text-sm font-bold", !isHome ? "text-red-400" : "text-stone-500")}>{game?.opponent_name || 'Opponent'}</p>
                {!isHome && <p className="text-red-600 text-[9px] mt-0.5">HOME (bats 2nd)</p>}
              </button>
            </div>

            {/* Little League Rest Rules toggle */}
            <div className="mt-3 pt-3 border-t border-stone-700 flex items-center justify-between gap-3" data-testid="setup-rest-rules-row">
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold flex items-center gap-1.5">
                  <Shield size={12} className="text-amber-400" />
                  Little League Rest Rules
                </p>
                <p className="text-stone-400 text-[10px] leading-tight mt-0.5">
                  {isRestEnforced(team)
                    ? 'Warns before using pitchers needing rest days.'
                    : 'Disabled — pitch count tracked only (softball / travel).'}
                </p>
              </div>
              <button
                type="button"
                className={cn(
                  "shrink-0 w-12 h-6 rounded-full relative transition-colors",
                  isRestEnforced(team) ? "bg-amber-600" : "bg-stone-600"
                )}
                onClick={handleRestRulesToggle}
                data-testid="setup-rest-rules-toggle"
                aria-label="Toggle Little League Rest Rules"
              >
                <span className={cn(
                  "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
                  isRestEnforced(team) ? "translate-x-6" : "translate-x-0.5"
                )} />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-4">
          {[
            { label: 'Lineup', done: lineupComplete },
            { label: 'Opp Lineup', done: oppLineupComplete },
            { label: 'Fielding', done: fieldComplete },
            { label: 'Opp Field', done: oppFieldComplete },
          ].map((step, i) => (
            <div key={i} className="flex-1">
              <div className={cn("h-1.5 rounded-full transition-all", step.done ? "bg-green-500" : "bg-stone-700")} />
              <p className={cn("text-[10px] mt-1 text-center", step.done ? "text-green-400" : "text-stone-600")}>{step.label}</p>
            </div>
          ))}
        </div>

        {/* Pitcher Availability (pre-game) — hidden when rest rules disabled or no pitchers */}
        {isRestEnforced(team) && (team?.players || []).some(p => p.is_pitcher) && (() => {
          const pitchers = (team?.players || []).filter(p => p.is_pitcher);
          const rows = pitchers.map(p => ({ p, s: getPitcherStatus(restMap, p.id) }));
          const counts = {
            ready: rows.filter(r => r.s.status === 'ready').length,
            caution: rows.filter(r => r.s.status === 'caution').length,
            locked: rows.filter(r => r.s.status === 'locked').length,
          };
          // Sort: locked first, then caution, then ready
          const order = { locked: 0, caution: 1, ready: 2 };
          rows.sort((a, b) => order[a.s.status] - order[b.s.status]);
          return (
            <Card className="border-0 bg-stone-800/50" data-testid="pitcher-availability-card">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white text-xs font-bold flex items-center gap-1.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <Shield size={14} className="text-amber-400" />
                    PITCHER AVAILABILITY
                  </p>
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="px-1.5 py-0.5 rounded bg-green-900/50 text-green-300 font-bold">{counts.ready} ready</span>
                    {counts.caution > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300 font-bold">{counts.caution} 1-day</span>}
                    {counts.locked > 0 && <span className="px-1.5 py-0.5 rounded bg-red-900/50 text-red-300 font-bold">{counts.locked} locked</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {rows.map(({ p, s }) => (
                    <div
                      key={p.id}
                      data-testid={`pitcher-avail-${p.id}`}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border text-xs",
                        s.status === 'ready' && "bg-green-900/20 border-green-700/40",
                        s.status === 'caution' && "bg-amber-900/20 border-amber-700/50",
                        s.status === 'locked' && "bg-red-900/25 border-red-700/60"
                      )}
                    >
                      <span className="w-6 h-6 rounded bg-stone-700 flex items-center justify-center text-white font-bold text-[10px] shrink-0">{p.jersey_number || '?'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{p.name}</p>
                        <p className={cn(
                          "text-[10px] truncate",
                          s.status === 'ready' && "text-green-400",
                          s.status === 'caution' && "text-amber-400",
                          s.status === 'locked' && "text-red-400"
                        )}>{s.detail}</p>
                      </div>
                      <span className={cn(
                        "text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0",
                        s.status === 'ready' && "bg-green-600 text-white",
                        s.status === 'caution' && "bg-amber-600 text-white",
                        s.status === 'locked' && "bg-red-600 text-white"
                      )} data-testid={`pitcher-avail-status-${p.id}`}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Section 1: Your Team Lineup */}
        <div className="space-y-2">
          <SectionHeader
            title="YOUR LINEUP"
            icon={Users}
            isOpen={openSections.lineup}
            onToggle={() => toggleSection('lineup')}
            complete={lineupComplete}
            count={battingOrder.length}
          />
          {openSections.lineup && (
            <Card className="border-0 bg-stone-800/50">
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-stone-400 text-xs">Tap to add, arrows to reorder</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-green-600 text-green-400 hover:bg-green-900/30"
                      onClick={() => { setScanTarget('team'); setScanOpen(true); }}
                      data-testid="scan-team-lineup-btn">
                      <ScanLine size={14} className="mr-1" /> Scan Card
                    </Button>
                  </div>
                </div>

                {/* Batting order */}
                {battingOrder.map((player, idx) => (
                  <div key={player.id} className="flex items-center gap-2 bg-stone-700/60 rounded-lg p-2"
                    data-testid={`lineup-player-${player.id}`}>
                    <span className="w-6 h-6 bg-green-700 text-white rounded text-xs flex items-center justify-center font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-white text-sm font-medium flex-1 truncate">{player.name}</span>
                    <span className="text-stone-500 text-xs">#{player.jersey_number || '—'}</span>
                    <button onClick={() => movePlayer(idx, -1)} disabled={idx === 0}
                      className="text-stone-500 hover:text-white disabled:opacity-20 p-0.5" data-testid={`lineup-up-${idx}`}>
                      <ArrowUp size={14} />
                    </button>
                    <button onClick={() => movePlayer(idx, 1)} disabled={idx === battingOrder.length - 1}
                      className="text-stone-500 hover:text-white disabled:opacity-20 p-0.5" data-testid={`lineup-down-${idx}`}>
                      <ArrowDown size={14} />
                    </button>
                    <button onClick={() => removeFromLineup(player)}
                      className="text-red-500/60 hover:text-red-400 p-0.5" data-testid={`lineup-remove-${player.id}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {/* Add player form — type name + number like opponent lineup */}
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Player name" value={newMyPlayerName} onChange={(e) => setNewMyPlayerName(e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white text-sm h-9 flex-1"
                    data-testid="my-player-name-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMyPlayer()} />
                  <Input placeholder="#" value={newMyPlayerNum} onChange={(e) => setNewMyPlayerNum(e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white text-sm h-9 w-16"
                    data-testid="my-player-number-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMyPlayer()} />
                  <Button size="sm" onClick={handleAddMyPlayer} className="bg-green-700 hover:bg-green-600 h-9 px-3"
                    data-testid="add-my-player-btn">
                    <Plus size={14} />
                  </Button>
                </div>

                {/* Bench players to add */}
                {benchPlayers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-700">
                    <p className="text-stone-500 text-xs mb-2">On roster — tap to add:</p>
                    <div className="flex flex-wrap gap-2">
                      {benchPlayers.map(player => (
                        <button key={player.id} onClick={() => addToLineup(player)}
                          className="flex items-center gap-1.5 bg-stone-700/40 border border-stone-600 rounded-lg px-2.5 py-1.5 text-stone-300 text-xs hover:border-green-600 hover:text-green-300 transition-colors"
                          data-testid={`bench-add-${player.id}`}>
                          <Plus size={12} />
                          <span>#{player.jersey_number || '—'}</span>
                          <span className="font-medium">{player.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Section 2: Opponent Lineup */}
        <div className="space-y-2">
          <SectionHeader
            title="OPPONENT LINEUP"
            icon={Users}
            isOpen={openSections.opponentLineup}
            onToggle={() => toggleSection('opponentLineup')}
            complete={oppLineupComplete}
            count={opponentLineup.length}
          />
          {openSections.opponentLineup && (
            <Card className="border-0 bg-stone-800/50">
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-stone-400 text-xs">Add opponent batters</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-amber-600 text-amber-400 hover:bg-amber-900/30"
                      onClick={() => setPasteOpen(true)} data-testid="paste-opp-lineup-btn">
                      <ClipboardPaste size={14} className="mr-1" /> Paste
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-green-600 text-green-400 hover:bg-green-900/30"
                      onClick={() => { setScanTarget('opponent'); setScanOpen(true); }}
                      data-testid="scan-opp-lineup-btn">
                      <ScanLine size={14} className="mr-1" /> Scan Card
                    </Button>
                  </div>
                </div>

                {opponentLineup.map((opp, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-stone-700/60 rounded-lg p-2"
                    data-testid={`opp-player-${idx}`}>
                    <span className="w-6 h-6 bg-red-700 text-white rounded text-xs flex items-center justify-center font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <button onClick={() => handleOppNameClick(opp)}
                      className="text-white text-sm font-medium flex-1 truncate text-left hover:text-amber-300 transition-colors cursor-pointer"
                      title="Tap to assign fielding position"
                      data-testid={`opp-name-click-${idx}`}>
                      {opp.name}
                    </button>
                    <span className="text-stone-500 text-xs">#{opp.number || '—'}</span>
                    <button onClick={() => moveOpponent(idx, -1)} disabled={idx === 0}
                      className="text-stone-500 hover:text-white disabled:opacity-20 p-0.5">
                      <ArrowUp size={14} />
                    </button>
                    <button onClick={() => moveOpponent(idx, 1)} disabled={idx === opponentLineup.length - 1}
                      className="text-stone-500 hover:text-white disabled:opacity-20 p-0.5">
                      <ArrowDown size={14} />
                    </button>
                    <button onClick={() => removeOpponent(idx)}
                      className="text-red-500/60 hover:text-red-400 p-0.5">
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {/* Add opponent form */}
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Name" value={newOppName} onChange={(e) => setNewOppName(e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white text-sm h-9 flex-1"
                    data-testid="opp-name-input"
                    onKeyDown={(e) => e.key === 'Enter' && addOpponent()} />
                  <Input placeholder="#" value={newOppNumber} onChange={(e) => setNewOppNumber(e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white text-sm h-9 w-16"
                    data-testid="opp-number-input"
                    onKeyDown={(e) => e.key === 'Enter' && addOpponent()} />
                  <Button size="sm" onClick={addOpponent} className="bg-red-700 hover:bg-red-600 h-9 px-3"
                    data-testid="add-opp-btn">
                    <Plus size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Section 3: Your Field Positions */}
        <div className="space-y-2">
          <SectionHeader
            title="YOUR FIELDING"
            icon={MapPin}
            isOpen={openSections.fieldPositions}
            onToggle={() => toggleSection('fieldPositions')}
            complete={fieldComplete}
            count={Object.keys(positions).length}
          />
          {openSections.fieldPositions && (
            <Card className="border-0 bg-stone-800/50">
              <CardContent className="p-3 space-y-2">
                <MiniFieldDiagram positions={positions} players={team?.players} isOpponent={false} />
                {battingOrder.length > 0 || team?.players?.length > 0 ? (
                  <>
                    <p className="text-stone-400 text-xs mt-3">Tap a position, then tap a player from your roster</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {FIELD_POSITIONS.map(pos => {
                        const playerId = positions[pos];
                        const player = playerId ? team?.players?.find(p => p.id === playerId) : null;
                        return (
                          <button key={pos}
                            className={cn("flex flex-col items-center gap-0.5 p-2 rounded-lg border text-xs transition-all",
                              assigningMyPos === pos ? "border-amber-500 bg-amber-900/30" :
                              player ? "border-green-700 bg-green-900/20" : "border-stone-700 bg-stone-800/50 hover:border-stone-600"
                            )}
                            onClick={() => setAssigningMyPos(assigningMyPos === pos ? null : pos)}
                            data-testid={`field-pos-${pos}`}
                          >
                            <span className={cn("font-bold text-[10px] rounded px-1.5 py-0.5",
                              pos === 'P' ? "bg-amber-600 text-white" : "bg-stone-600 text-stone-300"
                            )}>{pos}</span>
                            <span className={cn("truncate w-full text-center text-[9px]",
                              player ? "text-green-400" : "text-stone-600"
                            )}>{player?.name || '—'}</span>
                          </button>
                        );
                      })}
                    </div>
                    {assigningMyPos && (
                      <div className="bg-stone-900 rounded-lg p-2 border border-amber-700/50">
                        <p className="text-amber-400 text-[10px] font-bold mb-1.5">Select player for {assigningMyPos}:</p>
                        <div className="flex flex-wrap gap-1">
                          {team?.players?.map(p => {
                            const alreadyAssigned = Object.entries(positions).find(([pos, id]) => id === p.id && pos !== assigningMyPos);
                            // Show availability dot only for P picker when rest rules are on & player is a pitcher
                            const showDot = assigningMyPos === 'P' && isRestEnforced(team) && p.is_pitcher;
                            const st = showDot ? getPitcherStatus(restMap, p.id).status : null;
                            const dotColor = st === 'ready' ? 'bg-green-500' : st === 'caution' ? 'bg-amber-500' : st === 'locked' ? 'bg-red-500' : '';
                            return (
                              <button key={p.id}
                                className={cn("px-2 py-1 rounded text-[10px] font-semibold transition-all inline-flex items-center gap-1",
                                  positions[assigningMyPos] === p.id ? "bg-green-700 text-white" :
                                  alreadyAssigned ? "bg-stone-800 text-stone-600" : "bg-stone-700 text-white hover:bg-stone-600"
                                )}
                                onClick={() => { assignPosition(assigningMyPos, p.id); setAssigningMyPos(null); }}
                                data-testid={`my-roster-pick-${p.id}`}
                              >
                                {showDot && <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotColor)} data-testid={`roster-pick-dot-${p.id}`} />}
                                {p.name}{p.jersey_number ? ` #${p.jersey_number}` : ''}
                              </button>
                            );
                          })}
                          <button className="px-2 py-1 rounded text-[10px] bg-red-900/50 text-red-400 hover:bg-red-900"
                            onClick={() => { assignPosition(assigningMyPos, null); setAssigningMyPos(null); }}>Clear</button>
                          <button className="px-2 py-1 rounded text-[10px] bg-blue-900/50 text-blue-400 hover:bg-blue-900"
                            onClick={() => { setNewPitcherInput({ show: true, pos: assigningMyPos, name: '', isOpp: false }); setAssigningMyPos(null); }}>+ New Player</button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-stone-500 text-xs text-center py-4">Add players to your lineup first, then assign field positions here</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Section 4: Opponent Field Positions */}
        <div className="space-y-2">
          <SectionHeader
            title="OPPONENT FIELDING"
            icon={MapPin}
            isOpen={openSections.opponentFieldPositions}
            onToggle={() => toggleSection('opponentFieldPositions')}
            complete={oppFieldComplete}
            count={Object.keys(oppPositions).length}
          />
          {openSections.opponentFieldPositions && (
            <Card className="border-0 bg-stone-800/50">
              <CardContent className="p-3 space-y-2">
                <MiniFieldDiagram oppPositions={oppPositions} isOpponent={true} />
                {opponentLineup.length > 0 ? (
                  <>
                    <p className="text-stone-400 text-xs mt-3">Tap a position, then tap a player from the lineup</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {FIELD_POSITIONS.map(pos => (
                        <button key={pos}
                          className={cn("flex flex-col items-center gap-0.5 p-2 rounded-lg border text-xs transition-all",
                            assigningOppPos === pos ? "border-amber-500 bg-amber-900/30" :
                            oppPositions[pos] ? "border-green-700 bg-green-900/20" : "border-stone-700 bg-stone-800/50 hover:border-stone-600"
                          )}
                          onClick={() => setAssigningOppPos(assigningOppPos === pos ? null : pos)}
                          data-testid={`opp-field-pos-${pos}`}
                        >
                          <span className={cn("font-bold text-[10px] rounded px-1.5 py-0.5",
                            pos === 'P' ? "bg-red-600 text-white" : "bg-stone-600 text-stone-300"
                          )}>{pos}</span>
                          <span className={cn("truncate w-full text-center text-[9px]",
                            oppPositions[pos] ? "text-green-400" : "text-stone-600"
                          )}>{oppPositions[pos] || '—'}</span>
                        </button>
                      ))}
                    </div>
                    {assigningOppPos && (
                      <div className="bg-stone-900 rounded-lg p-2 border border-amber-700/50">
                        <p className="text-amber-400 text-[10px] font-bold mb-1.5">Select player for {assigningOppPos}:</p>
                        <div className="flex flex-wrap gap-1">
                          {opponentLineup.map((opp, i) => {
                            const alreadyAssigned = Object.entries(oppPositions).find(([p, n]) => n === opp.name && p !== assigningOppPos);
                            return (
                              <button key={i}
                                className={cn("px-2 py-1 rounded text-[10px] font-semibold transition-all",
                                  oppPositions[assigningOppPos] === opp.name ? "bg-green-700 text-white" :
                                  alreadyAssigned ? "bg-stone-800 text-stone-600" : "bg-stone-700 text-white hover:bg-stone-600"
                                )}
                                onClick={() => {
                                  assignOppPosition(assigningOppPos, opp.name);
                                  setAssigningOppPos(null);
                                }}
                                data-testid={`opp-lineup-pick-${i}`}
                              >
                                {opp.name}{opp.number ? ` #${opp.number}` : ''}
                              </button>
                            );
                          })}
                          <button className="px-2 py-1 rounded text-[10px] bg-red-900/50 text-red-400 hover:bg-red-900"
                            onClick={() => { assignOppPosition(assigningOppPos, ''); setAssigningOppPos(null); }}>Clear</button>
                          <button className="px-2 py-1 rounded text-[10px] bg-blue-900/50 text-blue-400 hover:bg-blue-900"
                            onClick={() => { setNewPitcherInput({ show: true, pos: assigningOppPos, name: '', isOpp: true }); setAssigningOppPos(null); }}>+ New Player</button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-stone-500 text-xs text-center py-4">Enter the opponent lineup first, then assign field positions here</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Start Game Button */}
        <div className="pt-4 pb-8 space-y-3">
          {/* Save reminder */}
          <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-3 text-center">
            <p className="text-amber-400 text-xs font-semibold">
              All changes auto-save as you go — you're all set to start!
            </p>
          </div>
          <Button
            onClick={handleStartGame}
            className="w-full h-14 bg-green-600 hover:bg-green-500 text-white font-extrabold text-lg rounded-xl shadow-lg shadow-green-900/30"
            data-testid="start-game-btn"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Play size={22} className="mr-2" />
            START SCORING
          </Button>
          <p className="text-center text-stone-600 text-xs mt-2">
            Enter what you have — fill in the rest during the game
          </p>
        </div>
      </div>

      {/* Scan Lineup Dialog */}
      {/* New Pitcher/Player Dialog */}
      <Dialog open={newPitcherInput.show} onOpenChange={o => { if (!o) setNewPitcherInput({ show: false, pos: null, name: '', isOpp: false }); }}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-sm">
              Add {newPitcherInput.isOpp ? 'Opponent' : ''} {newPitcherInput.pos || 'Player'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-stone-400 text-xs text-center">
              Type the player name to add to {newPitcherInput.isOpp ? 'opponent lineup' : 'your roster'} as {newPitcherInput.pos}
            </p>
            <input type="text" value={newPitcherInput.name} onChange={e => setNewPitcherInput(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Player name" className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="new-pitcher-input" autoFocus onKeyDown={e => { if (e.key === 'Enter') handleNewPitcherConfirm(); }} />
            <div className="flex gap-2">
              <Button className="flex-1 bg-stone-700" onClick={() => setNewPitcherInput({ show: false, pos: null, name: '', isOpp: false })}>Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 font-bold" onClick={handleNewPitcherConfirm} disabled={!newPitcherInput.name.trim()} data-testid="new-pitcher-confirm">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ScanLineupDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onPlayersScanned={handleScannedPlayers}
      />

      {/* Paste Opponent Lineup Dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardPaste size={18} className="text-amber-400" /> Paste Opponent Lineup
            </DialogTitle>
          </DialogHeader>
          <p className="text-stone-400 text-xs">Paste one player per line. Formats: "Name #5", "#12 Smith", "1. Jones"</p>
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
            placeholder={"#3 Johnson\n#7 Smith\n#12 Williams\nDavis\n#22 Brown"}
            rows={8}
            className="w-full bg-stone-700 border border-stone-600 text-white text-sm rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="paste-opponent-textarea" />
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-stone-600 text-stone-300" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button className="flex-1 bg-amber-600 hover:bg-amber-500" onClick={handlePasteOpponent}
              disabled={!pasteText.trim()} data-testid="paste-opponent-confirm">
              Add Players
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Opponent to Field Position Dialog */}
      <Dialog open={assignOppFieldDialog.open} onOpenChange={(open) => !open && setAssignOppFieldDialog({ open: false, oppName: '', oppNumber: '' })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-sm">
              Assign {assignOppFieldDialog.oppName} to a position
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {FIELD_POSITIONS.map(pos => (
              <Button key={pos} size="sm"
                className={cn(
                  "text-xs font-bold",
                  oppPositions[pos] === assignOppFieldDialog.oppName ? "bg-amber-600" : "bg-stone-700 hover:bg-stone-600 border border-stone-600"
                )}
                onClick={() => assignOppToField(pos)}
                data-testid={`assign-opp-field-${pos}`}>
                {pos}
                <span className="text-[9px] ml-1 text-stone-400">{oppPositions[pos] ? '(' + oppPositions[pos].split(' ')[0] + ')' : ''}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* PITCHER REST WARNING — Reassign or Unlock */}
      <Dialog open={pitcherRestWarn.open} onOpenChange={(o) => { if (!o) setPitcherRestWarn({ open: false, pitcher: null, status: null, pos: null }); }}>
        <DialogContent className="bg-stone-800 border-red-700 max-w-xs" data-testid="setup-pitcher-rest-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-center text-sm flex items-center justify-center gap-2">
              <Shield size={16} /> Pitcher Needs Rest
            </DialogTitle>
          </DialogHeader>
          <div className="text-stone-300 text-xs text-center pt-1">
            <p className="text-white font-bold text-sm">{pitcherRestWarn.pitcher?.name}</p>
            <p className="mt-1">{pitcherRestWarn.status?.detail}</p>
            <p className="mt-2 text-stone-400 text-[11px]">
              Coach's call — reassign to a rested pitcher, or unlock and use them anyway.
            </p>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs"
              onClick={() => {
                const pos = pitcherRestWarn.pos || 'P';
                setPitcherRestWarn({ open: false, pitcher: null, status: null, pos: null });
                setAssigningMyPos(pos); // reopen picker for this position
                toast.info('Pick a rested pitcher for P');
              }}
              data-testid="setup-pitcher-rest-reassign"
            >
              Reassign
            </Button>
            <Button
              className="flex-1 bg-red-700 hover:bg-red-600 text-white font-bold text-xs"
              onClick={() => {
                const { pitcher, pos } = pitcherRestWarn;
                setPitcherRestWarn({ open: false, pitcher: null, status: null, pos: null });
                if (pitcher && pos) {
                  doAssignPosition(pos, pitcher.id);
                  toast.success(`${pitcher.name} assigned as P (rest override)`);
                }
              }}
              data-testid="setup-pitcher-rest-unlock"
            >
              Unlock
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
